import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { KeyRound, RefreshCw, Save } from 'lucide-react';
import { appId, db } from '@/lib/firebase';
import { saveStripeConfig, syncStripeProducts, type StripeMode } from '@/lib/stripe-billing';

interface AdminStripeSettingsProps {
  currentUserRole: 'root' | 'admin' | 'moderator';
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

interface StripeSettings {
  publishableKey?: string;
  secretKey?: string;
  webhookSecret?: string;
  mode?: StripeMode;
  updatedBy?: string;
}

function maskValue(value: string): string {
  if (!value) return '';
  if (value.length < 8) return '********';
  return `${value.slice(0, 8)}...${value.slice(-4)}`;
}

export function AdminStripeSettings({
  currentUserRole,
  showNotify,
}: AdminStripeSettingsProps): JSX.Element {
  const [settings, setSettings] = useState<StripeSettings | null>(null);
  const [publishableKey, setPublishableKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [mode, setMode] = useState<StripeMode>('test');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'artifacts', appId, 'settings', 'stripe');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setSettings(null);
          return;
        }
        const data = snap.data() as StripeSettings;
        setSettings(data);
        setMode(data.mode === 'live' ? 'live' : 'test');
      },
      () => {
        setSettings(null);
      }
    );
    return () => unsub();
  }, []);

  const handleSave = async (): Promise<void> => {
    if (currentUserRole !== 'root') {
      showNotify?.('Only root admins can update Stripe credentials.', 'error');
      return;
    }
    if (!publishableKey.trim() || !secretKey.trim()) {
      showNotify?.('Publishable key and secret key are required.', 'error');
      return;
    }
    setSaving(true);
    try {
      await saveStripeConfig({
        publishableKey: publishableKey.trim(),
        secretKey: secretKey.trim(),
        webhookSecret: webhookSecret.trim(),
        mode,
      });
      setPublishableKey('');
      setSecretKey('');
      setWebhookSecret('');
      showNotify?.('Stripe credentials saved securely.', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save Stripe settings.';
      showNotify?.(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectionTest = async (): Promise<void> => {
    setTesting(true);
    try {
      const result = await syncStripeProducts();
      showNotify?.(`Connection successful. Synced ${result.synced} products.`, 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Stripe sync test failed.';
      showNotify?.(message, 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <section className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <KeyRound size={20} className="text-blue-600" />
          <h1 className="text-xl font-semibold text-gray-900">Stripe Settings</h1>
        </div>
        <p className="text-sm text-gray-600">
          Configure Stripe API credentials. Secret values are only used in Cloud Functions.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
        <div className="grid gap-5 md:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-700">Mode</span>
            <select
              value={mode}
              onChange={(event) => setMode(event.target.value as StripeMode)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="test">Test</option>
              <option value="live">Live</option>
            </select>
          </label>
        </div>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-gray-700">Publishable Key</span>
          <input
            type="text"
            value={publishableKey}
            onChange={(event) => setPublishableKey(event.target.value)}
            placeholder="pk_test_..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-gray-700">Secret Key</span>
          <input
            type="password"
            value={secretKey}
            onChange={(event) => setSecretKey(event.target.value)}
            placeholder="sk_test_..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-gray-700">Webhook Secret</span>
          <input
            type="password"
            value={webhookSecret}
            onChange={(event) => setWebhookSecret(event.target.value)}
            placeholder="whsec_..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </label>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || currentUserRole !== 'root'}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Stripe Credentials'}
          </button>
          <button
            type="button"
            onClick={handleConnectionTest}
            disabled={testing}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <RefreshCw size={16} className={testing ? 'animate-spin' : ''} />
            {testing ? 'Testing...' : 'Test Connection & Sync'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Stored Credentials (Masked)</h2>
        <div className="grid gap-3 text-sm text-gray-700">
          <p>
            <span className="font-medium">Mode:</span> {settings?.mode ?? 'not set'}
          </p>
          <p>
            <span className="font-medium">Publishable Key:</span>{' '}
            {settings?.publishableKey ? maskValue(settings.publishableKey) : 'not configured'}
          </p>
          <p>
            <span className="font-medium">Secret Key:</span>{' '}
            {settings?.secretKey ? maskValue(settings.secretKey) : 'not configured'}
          </p>
          <p>
            <span className="font-medium">Webhook Secret:</span>{' '}
            {settings?.webhookSecret ? maskValue(settings.webhookSecret) : 'not configured'}
          </p>
          <p>
            <span className="font-medium">Updated By:</span> {settings?.updatedBy ?? 'n/a'}
          </p>
        </div>
      </div>
    </section>
  );
}
